import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Shell } from '../components/Shell';
import { api, type ContractRow } from '../lib/api';
import { LEVEL, MODEL_LABEL, Meter, h, monthLabel, type LevelKey } from '../lib/contracts';

// Etapa 2 · tablero de contratos de soporte: cómo va cada bolsa, baseline y On Demand este mes.

function status(c: ContractRow): { level: LevelKey; pct: number; main: string; sub: string } {
  if (c.model === 'bag' && c.bag) {
    return { level: c.bag.level, pct: c.bag.pct, main: `${h(c.bag.used)} de ${h(c.bag.maxHours)}`, sub: `quedan ${h(c.bag.remaining)}` };
  }
  if (c.model === 'baseline' && c.baseline) {
    const cur = c.baseline.current;
    if (!cur) return { level: c.baseline.level, pct: 0, main: LEVEL[c.baseline.level].label, sub: '' };
    const extra = cur.outOfBand > 0 ? `${h(cur.outOfBand)} fuera de banda` : cur.inBand > 0 ? `${h(cur.inBand)} en banda` : `saldo ${h(cur.balance)}`;
    return { level: c.baseline.level, pct: cur.available > 0 ? cur.used / cur.available : 0, main: `${h(cur.used)} de ${h(cur.available)}`, sub: extra };
  }
  return { level: 'ok', pct: 0, main: `${h(c.thisMonth.charged)} este mes`, sub: 'se factura lo consumido' };
}

export default function Contracts() {
  const q = useQuery({ queryKey: ['contracts'], queryFn: () => api<{ contracts: ContractRow[] }>('/contracts'), staleTime: 60_000 });
  const rows = q.data?.contracts ?? [];
  const attention = rows.filter(r => ['alert', 'band', 'over', 'out'].includes(status(r).level) || r.baseline?.projectedExhaustMonth).length;

  return (
    <Shell>
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Contratos</h1>
          <p className="mt-1 text-sm text-muted">Saldo de cada contrato de soporte. Cuenta lo capturado en Veta Horas; los requerimientos cotizados descuentan lo cotizado.</p>
        </div>
        {q.isLoading ? <p role="status" className="py-10 text-center text-sm text-muted">Cargando…</p> : q.isError ? (
          <p role="alert" className="rounded-md bg-white p-6 text-sm text-[#8F1D1D]">{(q.error as Error).message}</p>
        ) : rows.length === 0 ? (
          <p className="rounded-lg border border-line bg-white p-8 text-center text-sm text-muted">No hay contratos de soporte activos. Se dan de alta en Veta Horas como proyecto de tipo Soporte.</p>
        ) : (
          <>
            {attention > 0 && <p className="rounded-md bg-[#FFF4E5] px-3 py-2 text-sm text-[#6A3A00]">{attention === 1 ? 'Un contrato requiere' : `${attention} contratos requieren`} atención este mes.</p>}
            <div className="overflow-x-auto rounded-lg border border-line bg-white">
              <table className="w-full min-w-[760px] border-collapse text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-muted">
                    <th scope="col" className="border-b border-line px-4 py-3 font-semibold">Cliente · proyecto</th>
                    <th scope="col" className="border-b border-line px-3 py-3 font-semibold">Modalidad</th>
                    <th scope="col" className="w-[280px] border-b border-line px-3 py-3 font-semibold">Consumo</th>
                    <th scope="col" className="border-b border-line px-3 py-3 font-semibold">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const s = status(r);
                    return (
                      <tr key={r.project.id} className="border-b border-[#EFEDE9] last:border-0 hover:bg-paper">
                        <td className="px-4 py-3">
                          <Link to={`/contratos/${r.project.id}`} className="font-medium text-ink hover:text-accent hover:underline">{r.project.clientName ?? '—'}</Link>
                          <p className="font-mono text-xs text-muted">{r.project.code} · {r.project.name}</p>
                        </td>
                        <td className="px-3 py-3 text-[#3F3D39]">{MODEL_LABEL[r.model]}{r.model === 'baseline' && r.baseline?.terms ? <span className="block text-xs text-muted">{h(r.baseline.terms.monthlyHours)}/mes · {r.baseline.terms.bandSource === 'pool' ? 'bolsa segmentada' : 'mensual estricto'}</span> : null}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-baseline justify-between gap-2 text-xs"><span className="font-semibold text-ink">{s.main}</span><span className="text-muted">{s.sub}</span></div>
                          {r.model !== 'on_demand' && <div className="mt-1.5"><Meter pct={s.pct} level={s.level} label={`Consumo de ${r.project.code}`} /></div>}
                          {r.model !== 'bag' && <p className="mt-1 text-[11px] text-muted">{monthLabel(r.month)}</p>}
                        </td>
                        <td className="px-3 py-3">
                          {r.model !== 'on_demand' && <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${LEVEL[s.level].chip}`}>{LEVEL[s.level].label}</span>}
                          {r.baseline?.projectedExhaustMonth && <p className="mt-1 text-xs text-[#8F1D1D]">A este ritmo se agota en {monthLabel(r.baseline.projectedExhaustMonth)}</p>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Shell>
  );
}
